/**
 * Version Configuration
 *
 * Centralized version information for the application.
 * Version is now injected from package.json at build time via Vite.
 */

// Version from package.json (injected at build time)
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '6.8.0';
const BUILD_DATE = import.meta.env.VITE_BUILD_DATE || new Date().toISOString().split('T')[0];
const COMMIT_HASH = import.meta.env.VITE_COMMIT_HASH || 'local';

// New features introduced in recent versions (for "New" badges)
export interface NewFeature {
  id: string;
  version: string;
  title: string;
  description: string;
  path?: string; // Navigation path if applicable
  icon?: string;
  expiresAt?: string; // Date when "New" badge disappears (ISO format)
}

export const NEW_FEATURES: NewFeature[] = [
  {
    id: 'wallet-multi-api',
    version: '5.1.0',
    title: 'Wallet Tracker Multi-API',
    description: 'DeBank, Alchemy & Moralis integration with smart data aggregation',
    path: '/wallet-tracker',
    icon: '🔗',
    expiresAt: '2025-12-18', // 2 weeks from release
  },
  {
    id: 'changelog-redesign',
    version: '5.1.0',
    title: 'Changelog Redesign',
    description: 'New timeline UI with search, collapsible sections, and stats',
    path: '/changelog',
    icon: '📋',
    expiresAt: '2025-12-18',
  },
  {
    id: 'fluxnode-dapps',
    version: '5.1.0',
    title: 'FluxNode dApps Monitor',
    description: 'Monitor dApps running on your FluxNodes',
    path: '/fluxnode',
    icon: '🖥️',
    expiresAt: '2025-12-18',
  },
  {
    id: 'keyboard-nav',
    version: '5.1.0',
    title: 'Keyboard Navigation',
    description: 'Cmd/Ctrl+Up/Down for quick page navigation',
    icon: '⌨️',
    expiresAt: '2025-12-18',
  },
];

export const VERSION_INFO = {
  // Semantic version (from package.json)
  version: APP_VERSION,

  // Current development phase
  phase: 'Phase F',
  phaseName: 'Multi-API & Documentation',

  // Current task/step
  currentTask: 'Phase F.2',
  taskName: 'Version Dynamique & Portfolio Snapshots',
  taskStatus: 'In Progress',

  // Build info (injected at build time)
  buildDate: BUILD_DATE,

  // Environment
  environment: import.meta.env.MODE || 'development',

  // Commit info
  commitHash: COMMIT_HASH,

  // Feature flags
  features: {
    multiPortfolio: true,
    realTimePrices: true,
    darkMode: true,
    mobileGestures: true,
    autopilot: true,
    walletTrackerMultiApi: true,
    authentication: false,
    alerts: false,
    socialFeatures: false,
    aiCopilot: false,
  }
} as const;

/**
 * Check if a feature is new (badge should be shown)
 */
export function isFeatureNew(featureId: string): boolean {
  const feature = NEW_FEATURES.find(f => f.id === featureId);
  if (!feature) return false;

  if (feature.expiresAt) {
    return new Date() < new Date(feature.expiresAt);
  }

  return true;
}

/**
 * Get all currently new features
 */
export function getNewFeatures(): NewFeature[] {
  const now = new Date();
  return NEW_FEATURES.filter(f => {
    if (f.expiresAt) {
      return now < new Date(f.expiresAt);
    }
    return true;
  });
}

/**
 * Get features new since a specific version
 */
export function getFeaturesSinceVersion(version: string): NewFeature[] {
  return NEW_FEATURES.filter(f => compareVersions(f.version, version) > 0);
}

/**
 * Compare two semantic versions
 * Returns: 1 if a > b, -1 if a < b, 0 if equal
 */
function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }

  return 0;
}

/**
 * Get formatted version string
 */
export function getVersionString(): string {
  return `v${VERSION_INFO.version}`;
}

/**
 * Get full version info string for footer
 */
export function getFullVersionString(): string {
  return `${getVersionString()} | ${VERSION_INFO.phase}: ${VERSION_INFO.taskName} (${VERSION_INFO.taskStatus})`;
}

/**
 * Get detailed version info for debugging
 */
export function getVersionDetails(): string {
  return [
    `Version: ${VERSION_INFO.version}`,
    `Phase: ${VERSION_INFO.phase} - ${VERSION_INFO.phaseName}`,
    `Task: ${VERSION_INFO.currentTask} - ${VERSION_INFO.taskName} (${VERSION_INFO.taskStatus})`,
    `Build: ${VERSION_INFO.buildDate}`,
    `Env: ${VERSION_INFO.environment}`,
    `Commit: ${VERSION_INFO.commitHash}`,
  ].join('\n');
}

/**
 * Log version info to console
 */
export function logVersionInfo(): void {
  console.log(
    `%c🚀 Edouard.ai ${getVersionString()}`,
    'font-size: 16px; font-weight: bold; color: #10b981;'
  );
  console.log(
    `%c${VERSION_INFO.phase}: ${VERSION_INFO.taskName} (${VERSION_INFO.taskStatus})`,
    'color: #6b7280;'
  );
  console.log(
    `%cBuild: ${VERSION_INFO.buildDate} | Env: ${VERSION_INFO.environment} | Commit: ${VERSION_INFO.commitHash}`,
    'color: #6b7280; font-size: 11px;'
  );

  // Log enabled features
  const enabledFeatures = Object.entries(VERSION_INFO.features)
    .filter(([, enabled]) => enabled)
    .map(([feature]) => feature);

  if (enabledFeatures.length > 0) {
    console.log(
      `%c✨ Features: ${enabledFeatures.join(', ')}`,
      'color: #8b5cf6; font-size: 11px;'
    );
  }

  // Log new features count
  const newFeatures = getNewFeatures();
  if (newFeatures.length > 0) {
    console.log(
      `%c🆕 New Features: ${newFeatures.length} (${newFeatures.map(f => f.title).join(', ')})`,
      'color: #f59e0b; font-size: 11px;'
    );
  }
}

/**
 * Check if this is a returning user (has completed onboarding or has version history)
 * First-time visitors should see onboarding, not the "What's New" modal
 */
export function isReturningUser(): boolean {
  // Check if onboarding was completed
  const onboardingCompleted = localStorage.getItem('portfolios') ||
                               localStorage.getItem('onboarding_completed') ||
                               localStorage.getItem('edouard_last_seen_version');
  return !!onboardingCompleted;
}

/**
 * Get last seen version from localStorage
 */
export function getLastSeenVersion(): string | null {
  return localStorage.getItem('edouard_last_seen_version');
}

/**
 * Set last seen version in localStorage
 */
export function setLastSeenVersion(version: string = VERSION_INFO.version): void {
  localStorage.setItem('edouard_last_seen_version', version);
}

/**
 * Check if user has seen the current version
 */
export function hasSeenCurrentVersion(): boolean {
  const lastSeen = getLastSeenVersion();
  if (!lastSeen) return false;
  return compareVersions(lastSeen, VERSION_INFO.version) >= 0;
}

/**
 * Get new features since user's last visit
 */
export function getNewFeaturesSinceLastVisit(): NewFeature[] {
  const lastSeen = getLastSeenVersion();
  if (!lastSeen) return getNewFeatures();
  return getFeaturesSinceVersion(lastSeen);
}
